/* eslint-disable @typescript-eslint/consistent-type-assertions */
import { assign, setup } from 'xstate5';
import { DietaryPropertyKind } from '@sg/graphql-schema';

import { mergeKnownProperties } from '../../utils';

// ─── Model ───────────────────────────────────────────────────────────────────

const initialDietaryRestrictions: DietaryRestrictionsTypes = {
  [DietaryPropertyKind.Dairy]: false,
  [DietaryPropertyKind.Meat]: false,
  [DietaryPropertyKind.Nuts]: false,
  [DietaryPropertyKind.Gluten]: false,
  [DietaryPropertyKind.Fish]: false,
  [DietaryPropertyKind.Soy]: false,
};

const model = setup({
  types: {
    context: {} as {
      restrictions: DietaryRestrictionsTypes;
      lastKnownRestrictions: DietaryRestrictionsTypes;
    },
    events: {} as
      | {
          type: 'TOGGLE_RESTRICTION';
          value: DietaryPropertyKind;
        }
      | {
          type: 'SET_RESTRICTIONS';
          value: DietaryRestrictionsTypes;
        }
      | {
          type: 'REVERT_RESTRICTIONS';
        }
      | {
          type: 'CLEAR_RESTRICTIONS';
        }
      | {
          type: 'CONFIRM_RESTRICTIONS';
        },
  },
});

// ─── Machine ─────────────────────────────────────────────────────────────────

export const dietaryPreferencesMachine = model.createMachine({
  id: 'dietaryPreferencesMachine',
  context: {
    restrictions: initialDietaryRestrictions,
    lastKnownRestrictions: initialDietaryRestrictions,
  },
  on: {
    TOGGLE_RESTRICTION: {
      actions: assign({
        restrictions({ context, event }) {
          const { restrictions } = context;
          return {
            ...restrictions,
            [event.value]: !restrictions[event.value],
          };
        },
      }),
    },
    SET_RESTRICTIONS: {
      actions: assign({
        restrictions({ context, event }) {
          const { restrictions } = context;
          const updatedRestrictions = mergeKnownProperties<
            DietaryRestrictionsTypes,
            Partial<DietaryRestrictionsTypes>
          >(restrictions, event.value);

          return updatedRestrictions;
        },
      }),
    },
    REVERT_RESTRICTIONS: {
      actions: assign({
        restrictions({ context }) {
          return context.lastKnownRestrictions;
        },
      }),
    },
    CLEAR_RESTRICTIONS: {
      actions: assign({
        restrictions: initialDietaryRestrictions,
        lastKnownRestrictions: initialDietaryRestrictions,
      }),
    },
    CONFIRM_RESTRICTIONS: {
      actions: assign({
        restrictions: ({ context }) => context.restrictions,
        lastKnownRestrictions: ({ context }) => context.restrictions,
      }),
    },
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

export type DietaryRestrictionsTypes = Record<DietaryPropertyKind, boolean>;
